import { useFacebookToken } from '@/hooks/useFacebookToken';
import { useTeams } from '@/hooks/useTeams';
import { supabase } from '@/lib/supabase';
import { callApi } from '@/utils/api';
import { getInstagramAccountQueryKey } from '@/utils/query-keys';
import { useQuery } from '@tanstack/react-query';

type InsightValue = {
  value: number;
  end_time: string;
};

type InsightData = {
  name: string;
  period: string;
  values: InsightValue[];
  title: string;
  description: string;
};

type Insights = {
  data: InsightData[];
  paging: {
    previous: string;
    next: string;
  };
};

export enum TimeFrame {
  Day = 'day',
  Week = 'week',
  Month = 'days_28',
}

type InstagramAccountOverviewResponse = {
  biography: string;
  followers_count: number;
  follows_count: number;
  id: string;
  insights: Insights;
  name: string;
  profile_picture_url: string;
  username: string;
  website: string;
};

export const useInstagramAccountOverview = (
  accountId: string,
  timeFrame: TimeFrame,
) => {
  const { needsReauthentication } = useFacebookToken();
  const { activeTeam } = useTeams();

  return useQuery({
    queryKey: getInstagramAccountQueryKey(accountId, timeFrame),
    queryFn: async (): Promise<InstagramAccountOverviewResponse> => {
      const { data, error } = await callApi(
        supabase,
        `/facebook/accounts/${accountId}?time_frame=${timeFrame}`,
      );

      if (error) throw error;

      return data;
    },
    enabled: !needsReauthentication || !activeTeam.data?.tokens?.facebook_token,
  });
};
